import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppBar, Toolbar, IconButton, Button, Typography, Box, Tooltip, List, ListItem, ListItemText, Divider } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SideDrawer from "./sideDrawer";
import { AuthButton } from "../shared/authButton";
import { StringsService } from "../services/strings/strings";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { auth } from "../firebase";
import { useTemporaryMenuCartProviderCart } from "../hooks/contexts/TemporaryMenuCartContext";
import "./index.css";
import { useTheme, useMediaQuery } from '@mui/material';
import { useSignedInState, useSignedInUserState } from "../hooks/useAuthState";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { formatMoney } from "../services/strings/formatters";

// A custom hook that builds on useLocation to parse the query string for you.
function usePathname() {
  const { pathname } = useLocation();
  return React.useMemo(() => pathname, [pathname]);
}

export type DrawerActionTypes = "side_drawer" | "dashboard";

type HeaderProps = {
  actionTypes: DrawerActionTypes;
  enableHeaderHomeNavigation: boolean;
};

export const headerHeight = '96px'
export const viewHightMinusHeader = 'calc(100vh - 96px)'


function Header({ actionTypes, enableHeaderHomeNavigation }: HeaderProps) {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [cartItemLabels, setCartItemLabels] = useState<{ [cartItemId: string]: string }>({})
  const [isLoading, setIsLoading] = useState(false)
  const { temporaryMenuCart, fetchTemporaryMenuCart } = useTemporaryMenuCartProviderCart();
  const navigate = useNavigate()
  let user = useSignedInUserState();
  let loggedInUser = useSignedInState();

  const toggleDrawer = (state: boolean) => setDrawerOpen(state);

  useEffect(() => {
    const getStrings = async () => {
      if (temporaryMenuCart) {
        let newCartItemLabels: { [cartItemId: string]: string } = {}
        const promises = temporaryMenuCart.items.map(async (element) => {
          newCartItemLabels[element.id] = `${element.name}`
        });
        await Promise.all(promises)
        setCartItemLabels(newCartItemLabels)
      }
    }
    // Cleanup subscription on unmount
    getStrings();
  }, [temporaryMenuCart]);

  useEffect(() => {
    fetchTemporaryMenuCart();
    console.log("running")
  }, [auth.currentUser]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ zIndex: 1000, boxShadow: 0, height: headerHeight }} className={"pat"} >
      <AppBar elevation={0} position="static" sx={{ backgroundColor: "transparent", color: "#000", alignContent: "center", padding: "16px" }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {
            actionTypes == "side_drawer" &&
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          }
          {
            actionTypes == "dashboard" &&
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => navigate("/dashboard")}
            >
              {/* USING BACK BUTTON FOR DASHBOARD */}
              <ArrowBackIcon />
            </IconButton>
          }
          <Typography
            variant="h6"
            component={enableHeaderHomeNavigation ? "a" : "div"}
            {...(enableHeaderHomeNavigation ? { href: "/" } : {})}
            align="center"
            sx={{
              position: "absolute",
              left: '50%',
              transform: "translateX(-50%)",
              flexGrow: 1,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <img
              src="/src_assets/header_logo.svg"
              alt="Eco Eats"
              style={{ height: "60px" }}
            />
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: isMobile ? 'flex-start' : 'center',
              flexShrink: 0,
            }}
          >
            <Tooltip
              onClick={() => { navigate("/order/confirm") }}
              title={
                temporaryMenuCart && temporaryMenuCart.items.length > 0 ? (
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Cart Items:
                    </Typography>
                    <List dense>
                      {temporaryMenuCart.items.map((item) => (
                        <React.Fragment key={item.id}>
                          <ListItem>
                            <ListItemText
                              primary={cartItemLabels[item.id]}
                              secondary={
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ color: "lightgray" }}
                                >
                                  ${formatMoney(item.price / 100)}
                                </Typography>
                              }
                            />
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                    <Typography variant="body2" align="right" sx={{ mt: 1 }}>
                      Total: {formatMoney((temporaryMenuCart.grandTotalBeforeFees || 0) / 100)}
                    </Typography>
                  </Box>
                ) : (
                  "Empty"
                )
              }
              arrow
              placement="bottom"
            >
              {temporaryMenuCart && temporaryMenuCart.grandTotalBeforeFees > 0 ? (
                <IconButton color="inherit" sx={{ ml: 1 }}>
                  <ShoppingCartIcon />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {formatMoney((temporaryMenuCart.grandTotalBeforeFees || 0) / 100)}
                  </Typography>
                </IconButton>
              ) : (
                <span />
              )}
            </Tooltip>

            {/* <AuthButton /> */}
          </Box>

          {/* <OneClickNavigation /> */}
        </Toolbar>
      </AppBar>
      <SideDrawer isOpen={isDrawerOpen} onClose={() => toggleDrawer(false)} />
    </Box>
  );
}

export { Header };
